import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, Loader2, CheckCircle2 } from "lucide-react";
import { submitGuestbook } from "../../lib/api";

const CATS = [
    { value: "thomas", label: "Thomas Rabitsch" },
    { value: "bernhard", label: "Bernhard Rabitsch" },
    { value: "general", label: "Allgemein" },
];

export default function GuestbookForm({ open, onClose, defaultCategory = "general", onSubmitted }) {
    const [name, setName] = useState("");
    const [category, setCategory] = useState(defaultCategory);
    const [year, setYear] = useState("");
    const [message, setMessage] = useState("");
    const [photo, setPhoto] = useState(null);
    const [consent, setConsent] = useState(false);
    const [website, setWebsite] = useState(""); // honeypot
    const [status, setStatus] = useState("idle"); // idle|loading|success|error
    const [err, setErr] = useState("");

    useEffect(() => {
        if (open) {
            setCategory(defaultCategory);
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [open, defaultCategory]);

    const reset = () => {
        setName("");
        setYear("");
        setMessage("");
        setPhoto(null);
        setConsent(false);
        setStatus("idle");
        setErr("");
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!name.trim() || message.trim().length < 3) {
            setErr("Name und Nachricht sind erforderlich.");
            return;
        }
        if (!consent) {
            setErr("Bitte bestätige die Einwilligung.");
            return;
        }
        setStatus("loading");
        const fd = new FormData();
        fd.append("name", name.trim());
        fd.append("category", category);
        if (year) fd.append("year", year);
        fd.append("message", message.trim());
        fd.append("consent", "true");
        fd.append("website", website); // honeypot
        if (photo) fd.append("photo", photo);
        try {
            await submitGuestbook(fd);
            setStatus("success");
            onSubmitted?.();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "string" ? d : "Etwas ist schiefgelaufen.");
            setStatus("idle");
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
                    onClick={onClose}
                    data-testid="guestbook-modal"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-[#0d0d0d] border border-white/10"
                    >
                        <button
                            onClick={onClose}
                            data-testid="guestbook-modal-close"
                            className="absolute top-4 right-4 z-10 w-10 h-10 grid place-items-center border border-white/10 hover:border-[var(--rb-primary)] text-zinc-400 hover:text-white transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        {status === "success" ? (
                            <SuccessPanel
                                onClose={() => {
                                    reset();
                                    onClose();
                                }}
                                onAgain={reset}
                            />
                        ) : (
                            <form onSubmit={submit} className="p-8 md:p-12" data-testid="guestbook-form">
                                <div className="text-xs uppercase tracking-[0.32em] text-[var(--rb-primary)] mb-3">
                                    Neuer Eintrag
                                </div>
                                <h2 className="font-display text-4xl md:text-5xl text-white leading-tight mb-2">
                                    Dein Eintrag
                                </h2>
                                <p className="text-zinc-400 text-sm md:text-base mb-8 max-w-md">
                                    Ein Konzert, ein Song, ein Moment. Erzähl uns davon.
                                </p>

                                {/* Honeypot */}
                                <input
                                    type="text"
                                    name="website"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    className="absolute -left-[9999px]"
                                    aria-hidden="true"
                                />

                                <div className="space-y-5">
                                    <Field label="Name / Anzeigename *">
                                        <input
                                            required
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            maxLength={80}
                                            data-testid="gb-name"
                                            className="w-full bg-black/50 border border-white/10 px-4 py-3 text-white outline-none focus:border-[var(--rb-primary)] transition"
                                        />
                                    </Field>

                                    <Field label="Wem widmest du deinen Eintrag? *">
                                        <div className="grid grid-cols-3 gap-2">
                                            {CATS.map((c) => (
                                                <button
                                                    type="button"
                                                    key={c.value}
                                                    onClick={() => setCategory(c.value)}
                                                    data-testid={`gb-cat-${c.value}`}
                                                    className={`py-3 text-xs md:text-sm uppercase tracking-[0.2em] border transition-all ${
                                                        category === c.value
                                                            ? "border-[var(--rb-primary)] text-white bg-[var(--rb-primary)]/10"
                                                            : "border-white/10 text-zinc-400 hover:text-white"
                                                    }`}
                                                >
                                                    {c.label.split(" ")[0]}
                                                </button>
                                            ))}
                                        </div>
                                    </Field>

                                    <Field label="Jahr der Erinnerung (optional)">
                                        <input
                                            type="number"
                                            min="1950"
                                            max={new Date().getFullYear()}
                                            value={year}
                                            onChange={(e) => setYear(e.target.value)}
                                            data-testid="gb-year"
                                            className="w-full bg-black/50 border border-white/10 px-4 py-3 text-white outline-none focus:border-[var(--rb-primary)] transition"
                                        />
                                    </Field>

                                    <Field label="Dein Eintrag *">
                                        <textarea
                                            required
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            rows={6}
                                            maxLength={4000}
                                            data-testid="gb-message"
                                            className="w-full bg-black/50 border border-white/10 px-4 py-3 text-white outline-none focus:border-[var(--rb-primary)] transition resize-none"
                                        />
                                        <div className="text-[10px] text-zinc-600 mt-1 text-right">
                                            {message.length}/4000
                                        </div>
                                    </Field>

                                    <Field label="Foto (optional, max. 5 MB)">
                                        <label
                                            className="flex items-center gap-3 bg-black/50 border border-white/10 hover:border-[var(--rb-primary)] px-4 py-3 cursor-pointer transition"
                                            data-testid="gb-photo-label"
                                        >
                                            <Upload className="w-4 h-4 text-zinc-400" />
                                            <span className="text-sm text-zinc-300">
                                                {photo ? photo.name : "JPG · PNG · WebP"}
                                            </span>
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                                                className="hidden"
                                                data-testid="gb-photo"
                                            />
                                        </label>
                                    </Field>

                                    <label className="flex items-start gap-3 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={consent}
                                            onChange={(e) => setConsent(e.target.checked)}
                                            data-testid="gb-consent"
                                            className="mt-1 accent-[var(--rb-primary)]"
                                        />
                                        <span className="text-xs md:text-sm text-zinc-400 leading-relaxed">
                                            Ich bestätige, dass ich berechtigt bin, diesen Text und
                                            ggf. dieses Foto einzureichen und einer Veröffentlichung
                                            auf rabitsch.supporter zustimme.
                                        </span>
                                    </label>

                                    <p className="text-[11px] text-zinc-600 leading-relaxed">
                                        Datenschutz-Hinweis: Deine Angaben werden ausschließlich zur
                                        Prüfung und Veröffentlichung deines Gästebuch-Eintrags
                                        gespeichert. Deine E-Mail-Adresse (falls angegeben) wird
                                        niemals öffentlich angezeigt. Es findet keine Weitergabe an
                                        Dritte statt.
                                    </p>
                                </div>

                                {err && (
                                    <div className="mt-5 text-sm text-[#ff8b8b]" data-testid="gb-error">
                                        {err}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={status === "loading"}
                                    data-testid="gb-submit"
                                    className="mt-8 w-full flex items-center justify-center gap-3 bg-[var(--rb-primary)] hover:bg-[var(--rb-primary-hover)] text-black uppercase tracking-[0.28em] text-sm py-4 transition-all hover:-translate-y-0.5 disabled:opacity-60"
                                >
                                    {status === "loading" ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        "Eintrag absenden"
                                    )}
                                </button>
                            </form>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

const Field = ({ label, children }) => (
    <div>
        <label className="block text-[10px] uppercase tracking-[0.32em] text-zinc-500 mb-2">
            {label}
        </label>
        {children}
    </div>
);

const SuccessPanel = ({ onClose, onAgain }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="p-10 md:p-16 text-center"
        data-testid="gb-success"
    >
        <div className="relative mx-auto w-20 h-20 mb-8">
            <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-20 h-20 rounded-full bg-[var(--rb-primary)]/15 grid place-items-center"
            >
                <CheckCircle2 className="w-9 h-9 text-[var(--rb-primary)]" strokeWidth={1.5} />
            </motion.div>
        </div>
        <div className="text-xs uppercase tracking-[0.32em] text-[var(--rb-primary)] mb-3">
            Vielen Dank für deinen Eintrag ♥
        </div>
        <h3 className="font-display text-3xl md:text-4xl text-white mb-4">
            Dein Eintrag ist angekommen.
        </h3>
        <p className="text-zinc-400 max-w-md mx-auto leading-relaxed mb-8">
            Dein Eintrag wurde erfolgreich übermittelt und wird zunächst geprüft.
            Sobald er freigegeben wurde, erscheint er im Gästebuch.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
                onClick={onAgain}
                className="px-6 py-3 border border-white/10 hover:border-[var(--rb-primary)] text-white uppercase tracking-[0.28em] text-xs transition"
            >
                Noch einen Eintrag
            </button>
            <button
                onClick={onClose}
                className="px-6 py-3 bg-[var(--rb-primary)] hover:bg-[var(--rb-primary-hover)] text-black uppercase tracking-[0.28em] text-xs transition"
            >
                Schließen
            </button>
        </div>
    </motion.div>
);
